import { getDisplayVersion } from '@ms-cloudpack/package-utilities';
import { globSourceFiles } from '@ms-cloudpack/path-utilities';
import path from 'path';
import { fileURLToPath, pathToFileURL } from 'url';
import type { CommandInitModule } from './types/CommandInitFunction.js';
import { PerfMarkerCliEntry } from './performance/markers.js';
import { CloudpackCommand, type CloudpackCommandParams } from './utilities/CloudpackCommand.js';
import { getNodeEngineRequirement } from './utilities/getNodeEngineRequirement.js';
import { satisfies } from 'semver';
import type { ProgramOptions } from './types/ProgramOptions.js';
import { createCommandEvents } from './utilities/commandEvents.js';

/** Override options for tests */
export type MainOptions = Partial<Pick<ProgramOptions, 'cwd' | 'argv' | 'commandEvents' | 'telemetryOptions'>> &
  Pick<CloudpackCommandParams<object, never>, 'outputOptions'>;

/**
 * Entry point for the cloudpack CLI.
 * Resolves all commands defined within the "./commands" folder.
 */
export async function main(options: Partial<MainOptions> = {}): Promise<void> {
  process.title = 'cloudpack';
  // Check Node.js version compatibility before doing anything else
  const requiredNodeVersion = getNodeEngineRequirement();
  if (requiredNodeVersion && !satisfies(process.version, requiredNodeVersion)) {
    console.error(`Cloudpack requires Node.js ${requiredNodeVersion}. Current version: ${process.version}.`);
    // eslint-disable-next-line no-restricted-properties -- exit immediately
    process.exit(1);
  }

  const {
    argv = [...process.argv],
    // eslint-disable-next-line no-restricted-properties -- this is where the default is supplied
    cwd = process.cwd(),
    telemetryOptions,
    outputOptions,
    commandEvents = createCommandEvents(),
  } = options;

  performance.mark(PerfMarkerCliEntry);

  if (argv[3] === '--') {
    // Remove the "--" argument in case it's added by lage
    argv.splice(3, 1);
  }

  const version = getDisplayVersion(import.meta.url);

  // Create the top-level command and apply common options
  // (avoids "unknown option" errors if they're specified without a verb)
  const program = new CloudpackCommand({
    name: 'cloudpack',
    description: 'A tool for managing fast inner and outer looping in web apps. Requires a sub-command.',
    usage: '<command> [options]',
    outputOptions,
    programOptions: { cwd, version, argv, telemetryOptions, commandEvents },
    // The top-level command should realistically only ever be used with --help or --version,
    // so hide most of the options.
    hideSharedOptions: true,
  });

  // Get all the available commands
  const commandsGlob = 'commands/*/index';
  const commands = await globSourceFiles(import.meta.url, commandsGlob);
  if (!commands.length) {
    const currentDir = path.dirname(fileURLToPath(import.meta.url));
    throw new Error(`No commands found matching "${currentDir}/${commandsGlob}.*".`);
  }

  for (const commandPath of commands) {
    const commandUrl = pathToFileURL(commandPath).toString();
    const commandModule = (await import(commandUrl)) as CommandInitModule;

    commandModule.init(program);
  }

  await program.run();
}
